    import { Button, Grid, Paper } from '@material-ui/core';
    import { Alert } from '@mui/material';
    import LoadingSpinner from 'components/LoadingSpinner';
    import NotificationProvider from 'components/NotificationProvider';
    import { groupLegTypes, operableTypesById, trailerTypesById } from 'features/loads/enums';
    import { useAddLoadGroupMutation } from 'features/loads/loadsSlice';
    import { setErrors, setLoadsErrors, setLoadsMissingFields, setMissingFields, setNewLots, setShowMandatoryError } from 'features/loads/loadsStateSlice';
    import { selectIsClient, selectUserClientId } from 'features/user/userSlice';
    import moment from 'moment';
    import { useEffect, useState } from 'react';
    import { useDispatch, useSelector } from 'react-redux';
    import { v4 as uuidv4 } from 'uuid';
    import { CreateLoadGroupStyles } from './CreateLoadGroupStyles';
    import GroupAssetForm from './GroupAssetForm/GroupAssetForm';
    import GroupLegsForm from './GroupLegsForm/GroupLegsForm';
    import LoadGroupJourney from './LoadGroupJourney';
    import { isValidPhoneNumber } from 'react-phone-number-input';
    import { useGetLotsQuery } from 'features/lots/lotSlice';

    function CreateLoadGroup({
        allowMultileg = true,
        loadData,
        setUseDialog,
        disableAssetDetails,
        selectedLegIndex, //Used to add a leg to an existing load, when set, we will only send this load to the API and it'll be added to the existing load
        onClose,
        vehicleData
    }) {
        let { data: lots, error: lotError, isLoading: isLoadingLots } = useGetLotsQuery();
        const classes = CreateLoadGroupStyles()
        const isClient = useSelector(selectIsClient);
        const loggedInClients = useSelector(selectUserClientId);
        const { newLots, errors, loadsErrors, loadsMissingFields, missingFields } = useSelector(state => state.loadState);
        let [addLoadGroup, { isLoading: isSaving }] = useAddLoadGroupMutation();

        const [loadGroup, setLoadGroup] = useState({ loads: [{ uuid: uuidv4(), index: 0, type: groupLegTypes.TRANSPORT, pickupLocationId: loadData?.pickupLocationId }], ...loadData });
        const [legIndex, setLegIndex] = useState(0);
        const [clientConfigs, setClientConfigs] = useState();
        const [clientBUs, setClientBUs] = useState([]);
        const [checkMissingFields, setCheckMissingFields] = useState(false);
        const [resetSearch, setResetSearch] = useState(false);

        const dispatch = useDispatch();

        const newLotsLookup = newLots?.reduce(function (map, obj) {
            map[obj.id] = obj;
            return map;
        }, {});

        const lotsLookup = lots?.reduce(function (map, obj) {
            map[obj.id] = obj;
            return map;
        }, {});

        useEffect(() => {
            if (selectedLegIndex) {
                setLegIndex(selectedLegIndex);
            }
        }, [selectedLegIndex])

        useEffect(() => {
            if (vehicleData) {
                const { vin, assetId, year, make, model, assetTypeId, fuelType, id, ...other } = vehicleData;
                const loads = [...loadGroup.loads];
                loads[legIndex] = {
                    ...loads[legIndex],
                    assetTypeId,
                    assets: [{
                        vin,
                        assetId,
                        year,
                        make,
                        model,
                        fuelType,
                        assetTypeId,
                        id: vehicleData?.id ?? vehicleData?.vehicleId,
                        vehicleId: vehicleData?.id ?? vehicleData?.vehicleId,
                        weightClass: vehicleData?.weightClass
                    }]
                }
                setLoadGroup({
                    ...loadGroup,
                    loads
                });
            }
        }, [vehicleData])

        useEffect(() => {
            dispatch(setNewLots([]));
            dispatch(setShowMandatoryError(false));
            dispatch(setErrors({}));
            dispatch(setLoadsErrors({}));
            dispatch(setMissingFields({}));
            dispatch(setLoadsMissingFields({}));
            setCheckMissingFields(false)
            setResetSearch(false);
        }, [])

        const reset = () => {
            setLegIndex(0)
            setLoadGroup({ loads: [{ uuid: uuidv4(), index: 0, type: groupLegTypes.TRANSPORT }] });
            dispatch(setNewLots([]));
            dispatch(setShowMandatoryError(false));
            dispatch(setErrors({}));
            dispatch(setLoadsErrors({}));
            dispatch(setMissingFields({}));
            dispatch(setLoadsMissingFields({}));
            setCheckMissingFields(false);
            setResetSearch(true);
        }

        const normalizeAssets = (load) => {
            if (Array.isArray(load?.assets) && load.assets.length) return load.assets;
            return [{
                vin: load?.vin ?? "",
                assetId: load?.assetId ?? "",
                year: load?.year ?? "",
                make: load?.make ?? "",
                model: load?.model ?? "",
                assetTypeId: load?.assetTypeId ?? null,
                fuelType: load?.fuelType ?? null,
                vehicleId: load?.vehicleId ?? 0,
                id: load?.vehicleId ?? 0,
                weightClass: load?.weightClass ?? null,
            }];
        };

        const isLoadValid = (load) => {
            var pickupOwnedByTenant = lotsLookup?.[load?.pickupLocationId]?.ownedByTenant;
            var deliveryOwnedByTenant = lotsLookup?.[load?.deliveryLocationId]?.ownedByTenant;
            
            const assets = normalizeAssets(load);

            const assetsValid = assets.length > 0 && assets.every((a, i) => (
            a?.vin &&
            a?.assetId &&
            a?.make &&
            a?.model &&
            a?.year &&
            a?.assetTypeId &&
            a?.fuelType &&
            a?.weightClass
            ));



            let isValid = (!!(
                (clientConfigs?.config?.transportConfig?.needByWeek ? load.neededByWeek : true) &&
                (clientConfigs?.config?.transportConfig?.handOffWeek ? load.handoffWeek : true) &&
                assetsValid &&
                load.configMoveType &&
                load.pickupStartDate && !loadsErrors?.[load?.uuid]?.['pickupStartDate'] &&
                (load.operableType == operableTypesById.INOPERABLE ? load.trailerType && !loadsErrors?.[load?.uuid]?.['trailerType'] : true) &&
                load.pickupStartDate && !loadsErrors?.[load?.uuid]?.['pickupStartDate'] &&
                load.deliveryStartDate && !loadsErrors?.[load?.uuid]?.['deliveryStartDate'] &&
                (pickupOwnedByTenant ||
                (load.pickupPocName && !loadsErrors?.[load?.uuid]?.['pickupPocName'] &&
                load.pickupPocEmail && !loadsErrors?.[load?.uuid]?.['pickupPocEmail'] &&
                load.pickupPocPhone && !loadsErrors?.[load?.uuid]?.['pickupPocPhone'] &&
                (!load.pickupPocPhone2 || !loadsErrors?.[load?.uuid]?.['pickupPocPhone2']) &&
                (!load.pickupPocPhone3 || !loadsErrors?.[load?.uuid]?.['pickupPocPhone3']))))) &&

                (deliveryOwnedByTenant ||
                (load.deliveryPocName && !loadsErrors?.[load?.uuid]?.['deliveryPocName'] &&
                load.deliveryPocEmail && !loadsErrors?.[load?.uuid]?.['deliveryPocEmail'] &&
                load.deliveryPocPhone && !loadsErrors?.[load?.uuid]?.['deliveryPocPhone'] &&
                (!load.deliveryPocPhone2 || !loadsErrors?.[load?.uuid]?.['deliveryPocPhone2']) &&
                (!load.deliveryPocPhone3 || !loadsErrors?.[load?.uuid]?.['deliveryPocPhone3']))) &&
                
                ((load.pickupLocationId && load.pickupLocationId !== 0 && !load?.originNewLot) ||
                    (load?.originNewLot &&
                        load.pickupName &&
                        (clientConfigs?.config?.transportConfig?.nodeNameRequired ? load.pickupNodeName : true) &&
                        load.pickupAddress &&
                        load.pickupCity &&
                        load.pickupState &&
                        load.pickupZip &&

                ((load.deliveryLocationId && load.deliveryLocationId !== 0 && !load?.destinationNewLot) ||
                    (load?.destinationNewLot &&
                        load.deliveryName &&
                        (clientConfigs?.config?.transportConfig?.nodeNameRequired ? load.deliveryNodeName : true) &&
                        load.deliveryAddress &&
                        load.deliveryCity &&
                        load.deliveryState &&
                        load.deliveryZip))))

            return isValid;
        }

        const isActivityValid = (activity) => {
            let isValid = (!!(
                activity.activityTypeId &&
                activity.activityItemId &&
                activity.lotId &&
                activity.activityDate
            ))

            return isValid;
        }

        const findMissingLoadFields = (load) => {
            var pickupOwnedByTenant = lotsLookup?.[load?.pickupLocationId]?.ownedByTenant;
            var deliveryOwnedByTenant = lotsLookup?.[load?.deliveryLocationId]?.ownedByTenant;
            
            let missingLoadFields = {};
            if (clientConfigs?.config?.transportConfig?.needByWeek && !load.neededByWeek) missingLoadFields = { ...missingLoadFields, ["neededByWeek"]: "Missing needed by week" };
            if (clientConfigs?.config?.transportConfig?.handOffWeek && !load.handoffWeek) missingLoadFields = { ...missingLoadFields, ["handoffWeek"]: "Missing hand off week" };
            if (!load.configMoveType) missingLoadFields = { ...missingLoadFields, ["configMoveType"]: "Missing move type" };
            if (!load.pickupStartDate) missingLoadFields = { ...missingLoadFields, ["pickupStartDate"]: "Missing pickup start date" };
            if (!load.deliveryStartDate) missingLoadFields = { ...missingLoadFields, ["deliveryStartDate"]: "Missing delivery start date" };
            if (!load.trailerType && load.operableType == operableTypesById.INOPERABLE) missingLoadFields = { ...missingLoadFields, ["trailerType"]: "Missing trailer type" };

            if(!deliveryOwnedByTenant)
            {
                if (!load.deliveryPocName) missingLoadFields = { ...missingLoadFields, ["deliveryPocName"]: "Missing delivery POC name" };
                if (!load.deliveryPocEmail) missingLoadFields = { ...missingLoadFields, ["deliveryPocEmail"]: "Missing delivery POC email" };
                if (!load.deliveryPocPhone) missingLoadFields = { ...missingLoadFields, ["deliveryPocPhone"]: "Missing delivery POC phone" };
                if (load?.deliveryPocPhone && !isValidPhoneNumber(load?.deliveryPocPhone)) missingLoadFields = { ...missingLoadFields, deliveryPocPhone: "Invalid delivery POC phone" };
                if (load?.deliveryPocPhone2 && !isValidPhoneNumber(load?.deliveryPocPhone2)) missingLoadFields = { ...missingLoadFields, deliveryPocPhone2: "Invalid delivery POC phone 2" };
                if (load?.deliveryPocPhone3 && !isValidPhoneNumber(load?.deliveryPocPhone3)) missingLoadFields = { ...missingLoadFields, deliveryPocPhone3: "Invalid delivery POC phone 3" };
            }


        if(!pickupOwnedByTenant)
            {
                if (!load.pickupPocName) missingLoadFields = { ...missingLoadFields, ["pickupPocName"]: "Missing pickup POC name" };
                if (!load.pickupPocEmail) missingLoadFields = { ...missingLoadFields, ["pickupPocEmail"]: "Missing pickup POC email" };
                if (!load.pickupPocPhone) missingLoadFields = { ...missingLoadFields, ["pickupPocPhone"]: "Missing pickup POC phone" };
                if (load?.pickupPocPhone && !isValidPhoneNumber(load?.pickupPocPhone)) missingLoadFields = { ...missingLoadFields, pickupPocPhone: "Invalid pickup POC phone" };
                if (load?.pickupPocPhone2 && !isValidPhoneNumber(load?.pickupPocPhone2)) missingLoadFields = { ...missingLoadFields, pickupPocPhone2: "Invalid pickup POC phone 2" };
                if (load?.pickupPocPhone3 && !isValidPhoneNumber(load?.pickupPocPhone3)) missingLoadFields = { ...missingLoadFields, pickupPocPhone3: "Invalid pickup POC phone 3" };
            }

            if (!load?.originNewLot && (!load.pickupLocationId || load.pickupLocationId == 0)) missingLoadFields = { ...missingLoadFields, ["pickupLocationId"]: "Missing origin location" };
            if (load?.originNewLot && !load.pickupName) missingLoadFields = { ...missingLoadFields, ["pickupName"]: "Missing origin location name" };
            if (load?.originNewLot && clientConfigs?.config?.transportConfig?.nodeNameRequired && !load.pickupNodeName) missingLoadFields = { ...missingLoadFields, ["pickupNodeName"]: "Missing origin node name" };

            if (load?.originNewLot && !load.pickupAddress) missingLoadFields = { ...missingLoadFields, ["pickupAddress"]: "Missing origin address" };
            if (load?.originNewLot && !load.pickupCity) missingLoadFields = { ...missingLoadFields, ["pickupCity"]: "Missing origin city" };
            if (load?.originNewLot && !load.pickupState) missingLoadFields = { ...missingLoadFields, ["pickupState"]: "Missing origin state" };
            if (load?.originNewLot && !load.pickupZip) missingLoadFields = { ...missingLoadFields, ["pickupZip"]: "Missing origin zip" };

            if (!load?.destinationNewLot && (!load.deliveryLocationId || load.deliveryLocationId == 0)) missingLoadFields = { ...missingLoadFields, ["deliveryLocationId"]: "Missing destination location" };
            if (load?.destinationNewLot && !load.deliveryName) missingLoadFields = { ...missingLoadFields, ["deliveryName"]: "Missing destination location name" };
            if (load?.destinationNewLot && clientConfigs?.config?.transportConfig?.nodeNameRequired && !load.deliveryNodeName) missingLoadFields = { ...missingLoadFields, ["deliveryNodeName"]: "Missing destination node name" };

            if (load?.destinationNewLot && !load.deliveryAddress) missingLoadFields = { ...missingLoadFields, ["deliveryAddress"]: "Missing destination address" };
            if (load?.destinationNewLot && !load.deliveryCity) missingLoadFields = { ...missingLoadFields, ["deliveryCity"]: "Missing destination city" };
            if (load?.destinationNewLot && !load.deliveryState) missingLoadFields = { ...missingLoadFields, ["deliveryState"]: "Missing destination state" };
            if (load?.destinationNewLot && !load.deliveryZip) missingLoadFields = { ...missingLoadFields, ["deliveryZip"]: "Missing destination zip" };
            
            //Asset fields
            const assets = normalizeAssets(load);

            if (!assets.length) {
            missingLoadFields = { ...missingLoadFields, assets: "At least 1 vehicle is required" };
            } else {
            assets.forEach((a, idx) => {
                const p = `vehicle_${idx + 1}_`; 

                if (!a?.vin) missingLoadFields = { ...missingLoadFields, [`${p}vin`]: `Vehicle ${idx + 1}: Missing VIN` };
                if (!a?.assetId) missingLoadFields = { ...missingLoadFields, [`${p}assetId`]: `Vehicle ${idx + 1}: Missing asset ID` };
                if (!a?.make) missingLoadFields = { ...missingLoadFields, [`${p}make`]: `Vehicle ${idx + 1}: Missing make` };
                if (!a?.model) missingLoadFields = { ...missingLoadFields, [`${p}model`]: `Vehicle ${idx + 1}: Missing model` };
                if (!a?.year) missingLoadFields = { ...missingLoadFields, [`${p}year`]: `Vehicle ${idx + 1}: Missing year` };
                if (!a?.assetTypeId) missingLoadFields = { ...missingLoadFields, [`${p}assetTypeId`]: `Vehicle ${idx + 1}: Missing asset type` };
                if (!a?.fuelType) missingLoadFields = { ...missingLoadFields, [`${p}fuelType`]: `Vehicle ${idx + 1}: Missing fuel type` };
                if (!a?.weightClass) missingLoadFields = { ...missingLoadFields, [`${p}weightClass`]: `Vehicle ${idx + 1}: Missing weight class` };
            });
}
            return missingLoadFields;
        }

        const findMissingActivityFields = (load) => {
            let missingActivityFields = {};
            if (!load.activityTypeId) missingActivityFields = { ...missingActivityFields, ["activityTypeId"]: "Missing activity type" };
            if (!load.activityItemId) missingActivityFields = { ...missingActivityFields, ["activityItemId"]: "Missing activity item" };
            if (!load.lotId) missingActivityFields = { ...missingActivityFields, ["lotId"]: "Missing lot" };
            if (!load.activityDate) missingActivityFields = { ...missingActivityFields, ["activityDate"]: "Missing activity date" };
            return missingActivityFields;
        }

        const findMissingFields = (loadGroup) => {
            let missingAssetFields = {};
            if (clientBUs?.length && !loadGroup.businessUnit) missingAssetFields = { ...missingAssetFields, ["businessUnit"]: "Missing business unit" };
            dispatch(setMissingFields({ ...missingAssetFields }));
            let missingFieldsByLoad = {};
            loadGroup?.loads?.forEach((leg, index) => {
                if (!selectedLegIndex || selectedLegIndex == index) {
                    if (leg?.type === groupLegTypes.TRANSPORT) {
                        let missingLoadFields = findMissingLoadFields(leg);
                        if (Object.values(missingLoadFields)) missingFieldsByLoad = { ...missingFieldsByLoad, [leg?.uuid]: missingLoadFields }
                    } else {
                        let missingFields = findMissingActivityFields(leg);
                        if (Object.values(missingFields)) missingFieldsByLoad = { ...missingFieldsByLoad, [leg?.uuid]: missingFields }
                    }
                }
            });
            dispatch(setLoadsMissingFields({ ...missingFieldsByLoad }));
        }

        const isValid = () => {
            const isAssetValid = !!(loadGroup &&
                (!clientBUs?.length || loadGroup.businessUnit)
            )
            if (!isAssetValid) return false;

            let isLegsValid = true
            loadGroup?.loads?.forEach((leg, index) => {
                let isLegValid = true;
                if (!selectedLegIndex || selectedLegIndex == index) {
                    if (leg?.type === groupLegTypes.TRANSPORT) {
                        isLegValid = isLoadValid(leg);
                    } else {
                        isLegValid = isActivityValid(leg);
                    }
                    if (!isLegValid) {
                        isLegsValid = isLegValid;
                    }
                }
            });
            return isLegsValid;
        }

        const buildAssetsArrayFromLoad = (load) => {
            if (Array.isArray(load?.assets) && load.assets.length) return load.assets;

            return [{
                vin: load?.vin ?? "",
                assetId: load?.assetId ?? "",
                year: load?.year ?? "",
                make: load?.make ?? "",
                model: load?.model ?? "",
                assetTypeId: load?.assetTypeId ?? null,
                fuelType: load?.fuelType ?? null,
                vehicleId: load?.vehicleId ?? null,
                id: load?.vehicleId ?? 0,
                weightClass: load?.weightClass ?? null,
            }];
        };

        const onSave = async () => {
            if (loggedInClients != "" && Array.isArray(loggedInClients)) {
                loadGroup.loads[0].clientId = parseInt(loggedInClients);
            }
            const validate = isValid();
            if (!validate) {
                setCheckMissingFields(true);
                findMissingFields(loadGroup);
                dispatch(setShowMandatoryError(true));

                return;
            }
            // if (!loadToEdit) {
            let activitiesToCreate = loadGroup?.loads?.filter(load => load?.type === groupLegTypes.ACTIVITY)?.map(activity => {
                return {
                    ...activity,
                    ["activityDate"]: new Date(moment(activity.activityDate).format('MMM DD, yyyy, hh:mm a') + ' UTC').toISOString()
                }
            });
            let loadsToCreate = loadGroup?.loads?.filter((load, index) => load?.type === groupLegTypes.TRANSPORT && (!selectedLegIndex || selectedLegIndex == index))?.map(load => {
                let loadToReturn = load;
                if (!load?.pickupNewLot && newLotsLookup?.[load?.pickupLocationId]) {
                    loadToReturn = { ...loadToReturn, ...newLotsLookup?.[load?.pickupLocationId], ["pickupLocationId"]: 0 }
                }
                if (!load?.deliveryNewLot && newLotsLookup?.[load?.deliveryLocationId]) {
                    loadToReturn = { ...loadToReturn, ...newLotsLookup?.[load?.deliveryLocationId], ["deliveryLocationId"]: 0 }
                }
                if (!load?.trailerType) {
                    loadToReturn = { ...loadToReturn, ["trailerType"]: trailerTypesById.TO_BE_DRIVEN };
                }
                if(!load?.operableType) {
                    loadToReturn = { ...loadToReturn, ["operableType"]: operableTypesById.OPERABLE };
                }
                loadToReturn = {
                    ...loadToReturn,
                    assets: buildAssetsArrayFromLoad(loadToReturn)
                };

                return loadToReturn;
            })

        

            let result = await addLoadGroup({ ...loadGroup, ["loads"]: loadsToCreate, ["activities"]: activitiesToCreate })
            if (result?.error) {
                NotificationProvider.error(result?.error?.data?.messages?.[0] ?? `Failed to create the load`);
            } else {
                NotificationProvider.success(`Successfully created the load`);
                reset();
                document.getElementById("createLoadForm").reset();
                document.getElementById("pickupLocationField").reset();
                document.getElementById("deliveryLocationField").reset();
                if (setUseDialog) { setUseDialog(false); }
                onClose?.(false);
            }
            // }
            // else {
            //     let result = await updateLoad(newLoad)
            //     if (result?.error) {
            //         NotificationProvider.error(`Failed to update the load`)
            //     } else {
            //         NotificationProvider.success(`Successfully updated the load`);

            //         setNewLoad(null)
            //         document.getElementById("createLoadForm").reset();
            //     }
            //     handleClose();
            // }
        }

        useEffect(() => {
            if (checkMissingFields) {
                findMissingFields(loadGroup);
            }
        }, [loadGroup, clientBUs])

        const errorsList = { ...errors, ...loadsErrors?.[loadGroup?.loads?.[legIndex]?.uuid] }
        const hasErrors = !!Object.values(errorsList)?.filter(v => v != null)?.length;
        const missingFieldsList = { ...missingFields, ...loadsMissingFields?.[loadGroup?.loads?.[legIndex]?.uuid] };
        const hasMissingFields = checkMissingFields && !!Object.values(missingFieldsList)?.filter(v => v != null)?.length;

        return (
            <div className={classes.paper}>

                {loadGroup?.loads?.[legIndex]?.type == groupLegTypes.TRANSPORT && <GroupAssetForm legIndex={legIndex} loadGroup={loadGroup} setLoadGroup={setLoadGroup} clientBUs={clientBUs} setClientBUs={setClientBUs} disableAssetDetails={disableAssetDetails} resetSearch={resetSearch} />}

                <GroupLegsForm loadGroup={loadGroup} legIndex={legIndex} key={legIndex} setLoadGroup={setLoadGroup} clientConfigs={clientConfigs} setClientConfigs={setClientConfigs} />

                {allowMultileg && <LoadGroupJourney legIndex={legIndex} setLegIndex={setLegIndex} loadGroup={loadGroup} setLoadGroup={setLoadGroup} />}

                {
                    (hasErrors || hasMissingFields) && <Grid item xs={2}>
                        <Paper className={classes.errorWrapper}>
                            <h4>Please correct the following errors</h4>
                            <div className={classes.errors}>
                                {Object.values(errorsList)?.filter(v => v != null)?.map(error => <><Alert icon={false} severity="error">{error}</Alert><br /></>)}
                                {checkMissingFields && Object.values(missingFieldsList)?.filter(v => v != null)?.map(error => <><Alert icon={false} severity="error">{error}</Alert><br /></>)}
                            </div>
                        </Paper>
                    </Grid>
                }

                {(hasErrors || hasMissingFields) && checkMissingFields && <div className={classes.errorMessage}>There are some missing or invalid fields, please correct the errors.</div>}

                <div className={classes.btnContainer}>
                    {onClose &&
                        <Button
                            className={classes.button}
                            color='primary'
                            variant='outlined'
                            onClick={() => onClose(false)}
                        >
                            Cancel
                        </Button>
                    }
                    <Button
                        className={classes.button}
                        variant='contained'
                        color='primary'
                        onClick={onSave}
                        disabled={isSaving}>
                        {isSaving ? "Saving..." : "Finish"}
                    </Button>
                </div>
                <LoadingSpinner loading={isSaving} />
            </div>
        );
    }

    export default CreateLoadGroup;